import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Customerdetailoverlay.css";
import { Mail } from "lucide-react";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import { fetchCustomerAllTransactionsUrl } from "../../../api/endpoints";

/*
  CustomerDetailOverlay
  ─────────────────────
  Props:
    customer      — customer document from DB (matches customerSchema)
    transactions  — array of transaction docs for this customer
    currency      — string e.g. "₦"
    onClose       — () => void
    onEdit        — (customer) => void  (optional)

  Usage:
    {selected && (
      <CustomerDetailOverlay
        customer={selected}
        transactions={customerTxs}
        currency="₦"
        onClose={() => setSelected(null)}
      />
    )}
*/

/* ─── Helpers ─── */
const fmt = (n) => Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

/* ─── Status config ─── */
const STATUS_MAP = {
  active: { label: "Active", bg: "#dcfce7", color: "#16a34a" },
  inactive: { label: "Inactive", bg: "#f3f4f6", color: "#6b7a99" },
  away: { label: "Away", bg: "#fef3c7", color: "#d97706" },
  archieved: { label: "Archived", bg: "#fee2e2", color: "#dc2626" },
};

const CHANNEL_MAP = {
  online: { label: "Online", bg: "#dbeafe", color: "#2563EB" },
  inStore: { label: "In Store", bg: "#ede9fe", color: "#7c3aed" },
};

/* ─── Icons ─── */
const Ico = ({ d, size = 16, color = "currentColor", stroke = 2 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth={stroke} strokeLinecap='round' strokeLinejoin='round'>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IconX = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);
const IconMail = () => <Ico d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22,6 12,13 2,6' />;
const IconPhone = () => (
  <Ico d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.63 2 2 0 0 1 1.99-2.18H6a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 5.99 5.99l1.26-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z' />
);
const IconMapPin = () => <Ico d={["M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z", "M12 7m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0"]} />;
const IconStore = () => <Ico d={["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"]} />;
const IconCalendar = () => <Ico d={["M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z", "M16 2v4 M8 2v4 M3 10h18"]} />;
const IconTx = () => <Ico d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M16 13H8", "M16 17H8", "M10 9H8"]} />;
const IconEdit = () => <Ico d={["M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7", "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"]} />;
const IconUser = () => <Ico d={["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", "M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"]} />;
const IconCash = () => <Ico d='M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />;

/* ─── Avatar ─── */
const AVATAR_COLORS = [
  ["#1e3a5f", "#4A90E2"],
  ["#14532d", "#16a34a"],
  ["#7c2d12", "#ea580c"],
  ["#4c1d95", "#7c3aed"],
  ["#164e63", "#0d9488"],
  ["#1e1b4b", "#6366f1"],
];
function getAvatarColors(name = "") {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function Avatar({ name, avatar, size = 64 }) {
  const [from, to] = getAvatarColors(name);
  /*  if (avatar) {
    return (
      <img
        src={avatar}
        alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "3px solid #fff", boxShadow: "0 2px 12px rgba(0,0,0,.15)" }}
      />
    );
  } */
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: from,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.32,
        fontWeight: 800,
        color: "#fff",
        border: "3px solid #fff",
        boxShadow: "0 2px 12px rgba(0,0,0,.15)",
        flexShrink: 0,
        letterSpacing: "-1px",
        fontFamily: "'Nunito', sans-serif",
      }}
    >
      {initials(name) || "?"}
    </div>
  );
}

/* ─── Stat card ─── */
function StatCard({ label, value, sub, accent }) {
  return (
    <div className='cdp-stat-card'>
      <div className='cdp-stat-label'>{label}</div>
      <div className='cdp-stat-value' style={accent ? { color: accent } : {}}>
        {value}
      </div>
      {sub && <div className='cdp-stat-sub'>{sub}</div>}
    </div>
  );
}

/* ─── Info row ─── */
function InfoRow({ icon, label, value }) {
  if (!value || value === "—") return null;
  return (
    <div className='cdp-info-row'>
      <div className='cdp-info-icon'>{icon}</div>
      <div className='cdp-info-body'>
        <div className='cdp-info-label'>{label}</div>
        <div className='cdp-info-value'>{value}</div>
      </div>
    </div>
  );
}

/* ─── Transaction card ─── */
function TxCard({ tx, currency }) {
  const op = tx.operationType || {};
  const td = tx.transactionDetails || {};
  const products = tx.products || [];
  const isLoan = op.whichOperation === "loan";
  const isReturned = td.deliveryStatus === "returned";
  const amount = isLoan ? (isReturned ? 0 : op.intialPayment || 0) : td.totalAmount || 0;

  return (
    <div className={`cdp-tx-card ${isReturned ? "cdp-tx-returned" : ""}`}>
      <div className='cdp-tx-top'>
        <div className='cdp-tx-left'>
          <div className='cdp-tx-id'>#{(tx.transID || tx._id || "—").slice(0, 12)}…</div>
          <div className='cdp-tx-date'>{fmtDateTime(td.date || tx.createdAt)}</div>
        </div>
        <div className='cdp-tx-right'>
          <div className='cdp-tx-amount' style={{ color: isReturned ? "#dc2626" : isLoan ? "#d97706" : "#16a34a" }}>
            {isReturned ? "-" : ""}
            {currency} {fmt(amount)}
          </div>
          <div className='cdp-tx-badges'>
            <span className={`cdp-badge ${isLoan ? "cdp-badge-loan" : "cdp-badge-cash"}`}>{isLoan ? "Loan" : "Cash"}</span>
            {isReturned && <span className='cdp-badge cdp-badge-ret'>Returned</span>}
            {td.paymentMode === "card" && <span className='cdp-badge cdp-badge-card'>Card</span>}
            {td.paymentMode === "mobilePay" && <span className='cdp-badge cdp-badge-mpay'>MPay</span>}
          </div>
        </div>
      </div>

      {products.length > 0 && (
        <div className='cdp-tx-products'>
          {products.slice(0, 4).map((p, i) => (
            <div className='cdp-tx-chip' key={p._id || p.id || i}>
              {p.productImage && (
                <img
                  src={p.productImage}
                  alt={p.name}
                  style={{ width: 16, height: 16, borderRadius: 3, objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <span>{p.name || "Product"}</span>
              {p.quantity > 1 && <span className='cdp-chip-qty'>×{p.quantity}</span>}
            </div>
          ))}
          {products.length > 4 && <div className='cdp-tx-chip cdp-chip-more'>+{products.length - 4} more</div>}
        </div>
      )}

      {isLoan && op.balance > 0 && (
        <div className='cdp-tx-balance-row'>
          <span className='cdp-tx-balance-label'>Outstanding</span>
          <span className='cdp-tx-balance-val'>
            {currency} {fmt(op.balance)}
          </span>
        </div>
      )}

      <div className='cdp-tx-footer'>
        <span className='cdp-tx-sold-by'>by {(tx.soldBy || {}).fullName || "System"}</span>
        {td.numberOfItems && (
          <span className='cdp-tx-items'>
            {td.numberOfItems} item{td.numberOfItems !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN OVERLAY COMPONENT
═══════════════════════════════════════ */
export default function CustomerDetailOverlay({ customer = {}, currency = "₦", onClose, onMessage }) {
  const overlayRef = useRef(null);
  const [tab, setTab] = useState("overview"); // "overview" | "transactions"
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchCustomerTransactions();
  }, [customer]);

  /* Lock body scroll */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  /* Escape key */
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  /* Backdrop click */
  const handleBackdrop = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  // fetch customers transactions

  const fetchCustomerTransactions = async () => {
    const url = fetchCustomerAllTransactionsUrl + customer.customerID;
    console.log("fetching transactions from ", url);

    try {
      let response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      const { message, transactions } = await response.json();
      setTransactions(transactions || []);
      if (!response.ok) {
        throw new Error(message || "Failed to fetch transactions");
      }
    } catch (error) {
      console.log(error);

      uiAlert("Failed to fetch transactions for this customer", "error");
    }
  };

  /* Derived stats */
  const txCount = transactions.length;
  const totalSpent = transactions.reduce((s, tx) => {
    const op = tx.operationType || {};
    const td = tx.transactionDetails || {};
    const isLoan = op.whichOperation === "loan";
    const isRet = td.deliveryStatus === "returned";
    return s + (isRet ? 0 : isLoan ? op.intialPayment || 0 : td.totalAmount || 0);
  }, 0);
  const totalOwed = transactions.reduce((s, tx) => {
    const op = tx.operationType || {};
    const td = tx.transactionDetails || {};
    return s + (td.deliveryStatus === "returned" ? 0 : op.balance || 0);
  }, 0);

  const status = STATUS_MAP[customer.status] || STATUS_MAP.active;
  const channel = CHANNEL_MAP[customer.purchaseChannel] || CHANNEL_MAP.inStore;

  return createPortal(
    <div className='cdp-overlay' ref={overlayRef} onClick={handleBackdrop}>
      <div className='cdp-panel' role='dialog' aria-modal='true' aria-label={`${customer.fullName} details`}>
        {/* ── HERO HEADER ── */}
        <div className='cdp-hero'>
          {/* Close */}
          <button className='cdp-close' onClick={onClose} aria-label='Close'>
            <IconX />
          </button>

          {/* Edit */}
          {onMessage && (
            <button className='cdp-edit-btn' onClick={() => onMessage(customer)}>
              <Mail /> Message
            </button>
          )}

          {/* Avatar + name */}
          <div className='cdp-hero-top'>
            <Avatar name={customer.fullName} avatar={customer.avartar} size={72} />

            <div className='cdp-hero-info'>
              <div className='cdp-hero-name'>{customer.fullName || "Unknown Customer"}</div>
              <div className='cdp-hero-meta'>
                <span className='cdp-status-badge' style={{ background: status.bg, color: status.color }}>
                  {status.label}
                </span>
                <span className='cdp-channel-badge' style={{ background: channel.bg, color: channel.color }}>
                  {channel.label}
                </span>
                {customer.isNewCustomer && <span className='cdp-new-badge'>New</span>}
              </div>
              <div className='cdp-hero-id'>ID: {(customer.customerID || "—").slice(0, 20)}…</div>
            </div>
          </div>

          {/* Stats strip */}
          <div className='cdp-stats-strip'>
            <StatCard label='Total Spent' value={`${currency} ${fmt(customer.totalPurchaseAmount || totalSpent)}`} accent='#16a34a' />
            <StatCard
              label='Amount Owed'
              value={`${currency} ${fmt(customer.amountOwned || totalOwed)}`}
              accent={(customer.amountOwned || totalOwed) > 0 ? "#d97706" : "#6b7a99"}
            />
            <StatCard label='Transactions' value={txCount} sub={customer.lastPurchaseDate ? `Last: ${fmtDate(customer.lastPurchaseDate)}` : undefined} />
          </div>
        </div>

        {/* ── TABS ── */}
        <div className='cdp-tabs'>
          <button className={`cdp-tab ${tab === "overview" ? "cdp-tab-active" : ""}`} onClick={() => setTab("overview")}>
            <IconUser /> Overview
          </button>
          <button className={`cdp-tab ${tab === "transactions" ? "cdp-tab-active" : ""}`} onClick={() => setTab("transactions")}>
            <IconTx /> Transactions
            {txCount > 0 && <span className='cdp-tab-count'>{txCount}</span>}
          </button>
        </div>

        {/* ── BODY ── */}
        <div className='cdp-body'>
          {/* OVERVIEW TAB */}
          {tab === "overview" && (
            <div className='cdp-overview'>
              <div className='cdp-section-label'>Contact</div>
              <div className='cdp-info-list'>
                <InfoRow icon={<IconMail />} label='Email' value={customer.email !== "No Email" ? customer.email : null} />
                <InfoRow icon={<IconPhone />} label='Phone' value={customer.phoneNumber !== "No phone number" ? customer.phoneNumber : null} />
                <InfoRow icon={<IconMapPin />} label='Address' value={customer.address !== "No Address" ? customer.address : null} />
              </div>

              <div className='cdp-section-label' style={{ marginTop: 18 }}>
                Purchase Info
              </div>
              <div className='cdp-info-list'>
                <InfoRow icon={<IconStore />} label='Store' value={(customer.storepurchasedFrom || {}).storeName} />
                <InfoRow icon={<IconCalendar />} label='Last Purchase' value={fmtDate(customer.lastPurchaseDate)} />
                <InfoRow icon={<IconCash />} label='Total Spent' value={`${currency} ${fmt(customer.totalPurchaseAmount)}`} />
              </div>

              {(customer.amountOwned || totalOwed) > 0 && (
                <>
                  <div className='cdp-section-label' style={{ marginTop: 18 }}>
                    Outstanding
                  </div>
                  <div className='cdp-owed-card'>
                    <div className='cdp-owed-label'>Amount Owed</div>
                    <div className='cdp-owed-amount'>
                      {currency} {fmt(customer.amountOwned || totalOwed)}
                    </div>
                    <div className='cdp-owed-sub'>Across {transactions.filter((tx) => (tx.operationType?.balance || 0) > 0).length} loan transaction(s)</div>
                  </div>
                </>
              )}

              <div className='cdp-section-label' style={{ marginTop: 18 }}>
                Account
              </div>
              <div className='cdp-info-list'>
                <InfoRow
                  icon={<IconUser />}
                  label='Customer Type'
                  value={customer.userType ? customer.userType.charAt(0).toUpperCase() + customer.userType.slice(1) : "Standard"}
                />
                <InfoRow icon={<IconCalendar />} label='Member Since' value={fmtDate(customer.createdAt)} />
              </div>
            </div>
          )}

          {/* TRANSACTIONS TAB */}
          {tab === "transactions" && (
            <div className='cdp-tx-list'>
              {transactions.length === 0 ? (
                <div className='cdp-empty'>
                  <div className='cdp-empty-icon'>🧾</div>
                  <div className='cdp-empty-title'>No transactions yet</div>
                  <div className='cdp-empty-sub'>This customer has no recorded transactions.</div>
                </div>
              ) : (
                <>
                  {/* Tx summary */}
                  <div className='cdp-tx-summary'>
                    <div className='cdp-tx-sum-cell'>
                      <div className='cdp-tx-sum-label'>Total transactions</div>
                      <div className='cdp-tx-sum-val'>{txCount}</div>
                    </div>
                    <div className='cdp-tx-sum-cell'>
                      <div className='cdp-tx-sum-label'>Total paid</div>
                      <div className='cdp-tx-sum-val' style={{ color: "#16a34a" }}>
                        {currency} {fmt(totalSpent)}
                      </div>
                    </div>
                    <div className='cdp-tx-sum-cell'>
                      <div className='cdp-tx-sum-label'>Outstanding</div>
                      <div className='cdp-tx-sum-val' style={{ color: totalOwed > 0 ? "#d97706" : "#6b7a99" }}>
                        {currency} {fmt(totalOwed)}
                      </div>
                    </div>
                  </div>

                  {transactions.map((tx, i) => (
                    <TxCard key={tx.transID || tx._id || i} tx={tx} currency={currency} />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
